import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Tag,
  TextArea,
  TextInput,
  Tile,
} from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { useNotification } from "../../../context";
import { useAwardableNegotiations } from "../../../hooks/useAwardableNegotiations";
import { useGenerateOc } from "../../../hooks/useGenerateOc";
import {
  AwardableNegotiationResponse,
  GeneratedOc,
  GenerateOcSelection,
} from "../../../types";
import { formatOrderTotal } from "../formatOrderTotal";
import { isPastDate } from "../ocOverdue";
import { buildPaymentMethodOptions, INCOTERM_OPTIONS } from "../termOptions";
import "./createOc.scss";

interface CandidateRow extends DataTableRow {
  supplier: string;
  order: React.ReactNode;
  currency: string;
  total: string;
  lines: number;
}

interface Terms {
  paymentTerm: string;
  paymentMethod: string;
  incoterm: string;
  notes: string;
}

const EMPTY_TERMS: Terms = {
  paymentTerm: "",
  paymentMethod: "",
  incoterm: "",
  notes: "",
};

/** The buyer either just generates the OCs, or generates and goes on to coordinate the sent ones. */
type GenerateMode = "generate" | "coordinate";

/**
 * An OC is worth a post-generation look when the backend flagged its requested date as already
 * past, or when VAT was computed (so the buyer sees net / IVA / gross before opening it).
 */
const needsReview = (oc: GeneratedOc): boolean =>
  oc.overdueWarning === true || oc.taxAmount != null;

/** (orderId, supplierUserId, currency) is the backend's key for one candidate / one OC. */
const candidateKey = (c: AwardableNegotiationResponse): string =>
  `${c.orderId}|${c.supplierUserId}|${c.currency}`;

/**
 * When the modal is launched from a single order (the Bid view's "Crear OC"), only that
 * negotiation's candidates are shown: a joint group matches on its `multipleOrderId`, a simple
 * order on its `orderId`. Absent a filter every awardable candidate is shown (the list-page entry).
 */
export interface CreateOcFilter {
  orderId?: string;
  multipleOrderId?: string;
}

const matchesFilter = (
  c: AwardableNegotiationResponse,
  filter: CreateOcFilter
): boolean =>
  filter.multipleOrderId != null
    ? c.multipleOrderId === filter.multipleOrderId
    : filter.orderId != null
      ? c.multipleOrderId == null && c.orderId === filter.orderId
      : true;

export interface CreateOcModalProps {
  open: boolean;
  onClose: () => void;
  /** Restrict the picker to one negotiation and pre-select its candidates. */
  filter?: CreateOcFilter;
}

/**
 * "Crear OC": pick one or more awarded negotiations (each row is one order/group + supplier +
 * currency, so a joint award appears once per supplier/currency), optionally fill draft terms
 * applied to every selected OC, and generate them as DRAFTs. Money is shown per row in its own
 * currency and never summed across rows.
 */
const CreateOcModal = ({ open, onClose, filter }: CreateOcModalProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const navigate = useNavigate();
  const {
    data: allCandidates = [],
    isLoading,
    isError,
  } = useAwardableNegotiations(open);
  const generate = useGenerateOc();

  // Scoped to one negotiation when launched from an order; otherwise the whole awardable list.
  const candidates = useMemo(
    () =>
      filter
        ? allCandidates.filter((c) => matchesFilter(c, filter))
        : allCandidates,
    [allCandidates, filter]
  );

  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
  const [terms, setTerms] = useState<Terms>(EMPTY_TERMS);
  // The chosen action, awaiting confirmation; null shows the selection view.
  const [pendingMode, setPendingMode] = useState<GenerateMode | null>(null);
  // Set once the OCs are generated and there is something to review (overdue date / VAT).
  const [created, setCreated] = useState<GeneratedOc[] | null>(null);
  // A filtered open pre-selects its candidates once; this guards against re-selecting after the
  // user clears them (and against the async candidate load re-running the effect).
  const didPreselect = useRef(false);

  // Every open starts from a clean selection and blank terms.
  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setTerms(EMPTY_TERMS);
      setCreated(null);
      setPendingMode(null);
      didPreselect.current = false;
    }
  }, [open]);

  // When scoped to an order, pre-select that negotiation's candidates once they've loaded.
  useEffect(() => {
    if (!open || !filter || didPreselect.current || candidates.length === 0)
      return;
    setSelectedIds(candidates.map(candidateKey));
    didPreselect.current = true;
  }, [open, filter, candidates]);

  const byKey = useMemo(
    () => new Map(candidates.map((c) => [candidateKey(c), c])),
    [candidates]
  );

  // Built from the stable keys with the live `t` (never PAYMENT_METHOD_LIST's import-time names).
  const paymentMethodOptions = useMemo(() => buildPaymentMethodOptions(t), [t]);

  const rows: CandidateRow[] = useMemo(
    () =>
      candidates.map((c) => ({
        id: candidateKey(c),
        supplier: c.supplierName ?? "-",
        order: (
          <span className="create-oc__order">
            {c.name ?? c.internalCode ?? c.orderId}
            {c.name && c.internalCode ? ` (${c.internalCode})` : ""}
          </span>
        ),
        currency: c.currency,
        total: formatOrderTotal(c.totalAmount, c.currency),
        lines: c.lineCount,
      })),
    [candidates]
  );

  const headers: DataTableColumn<CandidateRow>[] = [
    { key: "supplier", header: t("purchaseOrders.create.supplier") },
    { key: "order", header: t("purchaseOrders.create.order") },
    { key: "currency", header: t("purchaseOrders.create.currency") },
    { key: "total", header: t("purchaseOrders.create.total") },
    { key: "lines", header: t("purchaseOrders.create.lines") },
  ];

  const setTerm = (key: keyof Terms) => (value: string) =>
    setTerms((prev) => ({ ...prev, [key]: value }));

  // The confirmation summary is derived from the selection alone — the only truthful pre-generate
  // data. The auto-sent / pending-approval split depends on the company's tiers (which this user may
  // not even be allowed to read), so it is surfaced AFTER generation, from the real OC statuses.
  const selectedCandidates = selectedIds.flatMap((id) => {
    const c = byKey.get(String(id));
    return c ? [c] : [];
  });
  const totalsByCurrency = useMemo(() => {
    const totals = new Map<string, number>();
    selectedCandidates.forEach((c) =>
      totals.set(c.currency, (totals.get(c.currency) ?? 0) + c.totalAmount)
    );
    return [...totals.entries()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, byKey]);
  const anyOverdueSelected = selectedCandidates.some((c) =>
    isPastDate(c.requestedDeliveryDate)
  );

  const handleGenerate = (mode: GenerateMode) => {
    const optionalTerms = Object.fromEntries(
      Object.entries(terms)
        .map(([k, v]) => [k, v.trim()])
        .filter(([, v]) => v !== "")
    ) as Partial<Terms>;

    const selections: GenerateOcSelection[] = selectedIds.flatMap((id) => {
      const c = byKey.get(String(id));
      return c
        ? [
            {
              orderId: c.orderId,
              supplierUserId: c.supplierUserId,
              currency: c.currency,
              ...optionalTerms,
            },
          ]
        : [];
    });
    if (selections.length === 0) return;

    generate.mutate(
      { selections, coordinate: mode === "coordinate" },
      {
        onSuccess: (result) => {
          const ocs = result.purchaseOrders;
          openNotification(
            t("purchaseOrders.create.success", { count: ocs.length })
          );

          if (mode === "coordinate") {
            // Only the auto-sent (SENT) OCs can be coordinated now; the pending-approval ones wait.
            const sentIds = ocs
              .filter((oc) => oc.status === "SENT")
              .map((oc) => oc.id);
            const pending = ocs.length - sentIds.length;
            if (sentIds.length > 0) {
              if (pending > 0) {
                openNotification(
                  t("purchaseOrders.create.coordinatePendingNotice", {
                    count: pending,
                  })
                );
              }
              onClose();
              navigate(`/purchase-orders/coordinate?ids=${sentIds.join(",")}`);
              return;
            }
            // Nothing auto-sent: show what was created, with a notice that none can be coordinated yet.
            openNotification(t("purchaseOrders.create.coordinateNothing"));
            setPendingMode(null);
            setCreated(ocs);
            return;
          }

          if (ocs.some(needsReview)) {
            // Keep the modal open on a confirmation: the overdue warning must be seen, not toasted away.
            setPendingMode(null);
            setCreated(ocs);
            return;
          }
          onClose();
          // One OC created: open it. Several: stay on the list, where they all appear.
          if (ocs.length === 1) {
            navigate(`/purchase-orders/${ocs[0].id}`);
          }
        },
        onError: () => openNotification(t("purchaseOrders.create.error")),
      }
    );
  };

  const title = t("purchaseOrders.create.title");

  if (created) {
    const anyOverdue = created.some((oc) => oc.overdueWarning);
    const single = created.length === 1 ? created[0] : null;
    const autoSentCount = created.filter((oc) => oc.status === "SENT").length;
    const pendingCount = created.filter(
      (oc) => oc.status === "PENDING_APPROVAL"
    ).length;
    return (
      <ComposedModal
        open={open}
        size="md"
        aria-label={title}
        onClose={() => {
          onClose();
        }}
      >
        <ModalHeader title={t("purchaseOrders.create.resultTitle")} />
        <ModalBody>
          {anyOverdue ? (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title={t("purchaseOrders.create.overdueTitle")}
              subtitle={t("purchaseOrders.create.overdueWarning")}
            />
          ) : null}
          {autoSentCount > 0 || pendingCount > 0 ? (
            <ul className="create-oc__result-summary">
              {autoSentCount > 0 ? (
                <li>
                  {t("purchaseOrders.create.resultAutoSent", {
                    count: autoSentCount,
                  })}
                </li>
              ) : null}
              {pendingCount > 0 ? (
                <li>
                  {t("purchaseOrders.create.resultPending", {
                    count: pendingCount,
                  })}
                </li>
              ) : null}
            </ul>
          ) : null}
          <div className="create-oc__result">
            {created.map((oc) => (
              <Tile key={oc.id} className="create-oc__result-item">
                <strong>{oc.code}</strong>
                {(oc.supplier?.companyName ?? oc.supplier?.name) ? (
                  <span className="create-oc__result-supplier">
                    {oc.supplier?.companyName ?? oc.supplier?.name}
                  </span>
                ) : null}
                {oc.currency && oc.totalAmount != null ? (
                  <dl className="create-oc__amounts">
                    <div>
                      <dt>{t("purchaseOrders.detail.net")}</dt>
                      <dd>{formatOrderTotal(oc.totalAmount, oc.currency)}</dd>
                    </div>
                    {oc.taxAmount != null ? (
                      <div>
                        <dt>{t("purchaseOrders.detail.tax")}</dt>
                        <dd>{formatOrderTotal(oc.taxAmount, oc.currency)}</dd>
                      </div>
                    ) : null}
                    {oc.grossAmount != null ? (
                      <div>
                        <dt>{t("purchaseOrders.detail.gross")}</dt>
                        <dd>{formatOrderTotal(oc.grossAmount, oc.currency)}</dd>
                      </div>
                    ) : null}
                  </dl>
                ) : null}
                {oc.overdueWarning ? (
                  <Tag type="red" size="sm">
                    {t("purchaseOrders.create.overdueTag")}
                  </Tag>
                ) : null}
              </Tile>
            ))}
          </div>
        </ModalBody>
        <ModalFooter
          secondaryButtonText={single ? t("cta.close") : undefined}
          primaryButtonText={
            single
              ? t("purchaseOrders.create.openOc")
              : t("purchaseOrders.create.done")
          }
          onRequestClose={onClose}
          onRequestSubmit={() => {
            onClose();
            if (single) navigate(`/purchase-orders/${single.id}`);
          }}
        >
          {null}
        </ModalFooter>
      </ComposedModal>
    );
  }

  if (pendingMode) {
    return (
      <ComposedModal
        open={open}
        size="sm"
        aria-label={t("purchaseOrders.create.confirmTitle")}
        onClose={onClose}
      >
        <ModalHeader title={t("purchaseOrders.create.confirmTitle")} />
        <ModalBody>
          <p className="create-oc__confirm-intro">
            {t("purchaseOrders.create.confirmIntro", {
              count: selectedCandidates.length,
            })}
          </p>
          <dl className="create-oc__confirm-totals">
            {totalsByCurrency.map(([currency, amount]) => (
              <div key={currency}>
                <dt>{currency}</dt>
                <dd>{formatOrderTotal(amount, currency)}</dd>
              </div>
            ))}
          </dl>
          {anyOverdueSelected ? (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title={t("purchaseOrders.create.confirmOverdueTitle")}
              subtitle={t("purchaseOrders.create.confirmOverdue")}
            />
          ) : null}
          <p className="create-oc__confirm-note">
            {t("purchaseOrders.create.confirmApprovalNote")}
          </p>
          {pendingMode === "coordinate" ? (
            <p className="create-oc__confirm-note">
              {t("purchaseOrders.create.confirmCoordinateNote")}
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            kind="secondary"
            onClick={() => setPendingMode(null)}
            disabled={generate.isPending}
          >
            {t("purchaseOrders.create.back")}
          </Button>
          <Button
            kind="primary"
            onClick={() => handleGenerate(pendingMode)}
            disabled={generate.isPending}
          >
            {pendingMode === "coordinate"
              ? t("purchaseOrders.create.confirmGenerateAndCoordinate")
              : t("purchaseOrders.create.confirmGenerate")}
          </Button>
        </ModalFooter>
      </ComposedModal>
    );
  }

  return (
    <ComposedModal
      open={open}
      size="lg"
      aria-label={title}
      onClose={() => {
        onClose();
      }}
    >
      <ModalHeader title={title} />
      <ModalBody hasScrollingContent>
        <p className="create-oc__intro">{t("purchaseOrders.create.intro")}</p>

        {isError ? (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={t("purchaseOrders.create.loadError")}
          />
        ) : (
          <DataTable
            headers={headers}
            rows={rows}
            isLoading={isLoading}
            selection={{
              selectedIds,
              onChange: setSelectedIds,
              getRowLabel: (row) =>
                t("purchaseOrders.create.selectRow", {
                  supplier: row.supplier,
                }),
              selectAllLabel: t("purchaseOrders.create.selectAll"),
            }}
            empty={
              <EmptyState
                title={t("purchaseOrders.create.empty")}
                description={t("purchaseOrders.create.emptyDescription")}
              />
            }
          />
        )}

        <fieldset className="create-oc__terms">
          <legend className="create-oc__terms-legend">
            {t("purchaseOrders.create.termsTitle")}
          </legend>
          <p className="create-oc__terms-hint">
            {t("purchaseOrders.create.termsHint")}
          </p>
          <div className="create-oc__terms-grid">
            <TextInput
              id="create-oc-payment-term"
              labelText={t("purchaseOrders.create.paymentTerm")}
              value={terms.paymentTerm}
              onChange={(e) => setTerm("paymentTerm")(e.target.value)}
            />
            <Select
              id="create-oc-payment-method"
              labelText={t("purchaseOrders.create.paymentMethod")}
              value={terms.paymentMethod}
              onChange={(e) => setTerm("paymentMethod")(e.target.value)}
            >
              <SelectItem value="" text={t("cta.select")} />
              {paymentMethodOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} text={o.label} />
              ))}
            </Select>
            <Select
              id="create-oc-incoterm"
              labelText={t("purchaseOrders.create.incoterm")}
              value={terms.incoterm}
              onChange={(e) => setTerm("incoterm")(e.target.value)}
            >
              <SelectItem value="" text={t("cta.select")} />
              {INCOTERM_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} text={o.label} />
              ))}
            </Select>
          </div>
          <TextArea
            id="create-oc-notes"
            labelText={t("purchaseOrders.create.notes")}
            rows={3}
            value={terms.notes}
            onChange={(e) => setTerm("notes")(e.target.value)}
          />
        </fieldset>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          {t("cta.cancel")}
        </Button>
        <Button
          kind="tertiary"
          disabled={selectedIds.length === 0}
          onClick={() => setPendingMode("generate")}
        >
          {t("purchaseOrders.create.generate", { count: selectedIds.length })}
        </Button>
        <Button
          kind="primary"
          disabled={selectedIds.length === 0}
          onClick={() => setPendingMode("coordinate")}
        >
          {t("purchaseOrders.create.generateAndCoordinate")}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default CreateOcModal;
